import React, {
  ComponentType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { PersonAddAlt } from "@mui/icons-material";
import { toastr } from "react-redux-toastr";

import PageLayout from "@/components/layouts/page/PageLayout";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";

import UserManagementMetricCard from "../components/UserManagementMetricCard";
import UserManagementTable from "../components/UserManagementTable";
import AddSubUserModal from "../components/AddSubUserModal";
import UserPermissionsModal from "../components/UserPermissionsModal";
import ResetPasswordModal from "../components/ResetPasswordModal";
import UserActionConfirmModal from "../components/UserActionConfirmModal";
import { userManagementService } from "../services/userManagementService";
import {
  USER_MANAGEMENT_ACTIONS,
  USER_MANAGEMENT_ACTION_LABELS,
  USER_MANAGEMENT_METRICS,
} from "../constants/userManagementConstants";
import { addSubUserInitialValues } from "../utils/userManagementValidators";

type ContextId = string | number;

type UserManagementApiUser = {
  actions?: string[];
  can_manage?: boolean;
  created_at?: string;
  createdAt?: string;
  date_added?: string;
  dateAdded?: string;
  effective_participant_id?: ContextId;
  email?: string;
  first_name?: string;
  id?: ContextId;
  job_title?: string;
  jobTitle?: string;
  last_login?: string;
  last_login_at?: string;
  lastLogin?: string;
  last_name?: string;
  mobile_no?: string;
  name?: string;
  participantData?: { id?: ContextId };
  participant_id?: ContextId;
  participant_sub_user_id?: ContextId;
  participantSubUserId?: ContextId;
  phoneNumber?: string;
  read_only?: boolean;
  status?: string | number;
  status_label?: string;
  sub_participant_id?: ContextId;
  type?: string;
  user_id?: ContextId;
  [key: string]: unknown;
};

type UserManagementRow = Omit<UserManagementApiUser, "actions"> & {
  actions: Array<{ label: string; value: string }>;
  dateAdded: string;
  email: string;
  firstName: string;
  id?: ContextId;
  jobTitle: string;
  lastLogin: string;
  lastName: string;
  name: string;
  participantSubUserId?: ContextId;
  phoneNumber: string;
  role: string;
  status: string | number;
  subUserId?: ContextId;
};

type UserManagementSummary = {
  active_users?: number;
  remaining_team_member_slots?: number;
  total_users?: number;
  user_limit?: number;
};

type PaginationState = {
  currentPage: number;
  pageSize: number;
  searchKey: string;
  totalPages: number;
  totalRecords: number;
};

type FetchUsersArgs = {
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
};

type ModalValues = {
  email: string;
  firstName: string;
  jobTitle?: string;
  lastName: string;
  phoneNumber?: string;
};

type ConfirmAction = {
  action: string;
  user: UserManagementRow;
};

type ServiceResponse = {
  data?: {
    pageCount?: number;
    recordsFiltered?: number;
    recordsTotal?: number;
    summary?: UserManagementSummary;
    total?: number;
    totalPages?: number;
    totalRecords?: number;
    users?: UserManagementApiUser[];
  };
};

type ServiceError = {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
};

type UserManagementContext = {
  loggedInUser?: UserManagementApiUser & {
    is_sub_participant?: boolean;
  };
  selectedBrand?: {
    id?: ContextId;
  } | null;
};

const PageLayoutComponent = PageLayout as ComponentType<any>;
const UserManagementMetricCardComponent =
  UserManagementMetricCard as ComponentType<any>;
const UserManagementTableComponent = UserManagementTable as ComponentType<any>;
const AddSubUserModalComponent = AddSubUserModal as ComponentType<any>;
const UserPermissionsModalComponent =
  UserPermissionsModal as ComponentType<any>;
const ResetPasswordModalComponent = ResetPasswordModal as ComponentType<any>;
const UserActionConfirmModalComponent =
  UserActionConfirmModal as ComponentType<any>;

const formatDate = (value?: string, emptyText = "-") => {
  if (!value) return emptyText;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const getParticipantId = (user: UserManagementApiUser = {}) =>
  user.effective_participant_id ||
  user.participantData?.id ||
  user.participant_id ||
  "";

const getSubUserId = (user: UserManagementApiUser = {}) =>
  user.participant_sub_user_id ||
  user.participantSubUserId ||
  user.sub_participant_id ||
  user.id;

const splitName = (name = "") => {
  const [firstName = "", ...rest] = name.replace(" (you)", "").split(" ");
  return {
    firstName,
    lastName: rest.join(" "),
  };
};

const getRoleLabel = (type?: string) => {
  if (type === "main_participant") return "Admin";
  if (type === "team_member") return "User";
  return "-";
};

const getActionItems = (
  user: UserManagementApiUser = {},
  isTeamMember: boolean
) => {
  if (user.read_only || !user.can_manage || isTeamMember) return [];
  const actions = user.actions || [];

  return actions
    .filter((action) => USER_MANAGEMENT_ACTION_LABELS[action])
    .map((action) => ({
      value: action,
      label: USER_MANAGEMENT_ACTION_LABELS[action],
    }));
};

const mapUserRow = (
  user: UserManagementApiUser,
  isTeamMember: boolean
): UserManagementRow => {
  const statusLabel = user.status_label || user.status || "-";
  const status = statusLabel;
  const isMainParticipant = user.type === "main_participant" || user.read_only;
  const { firstName, lastName } = splitName(user.name);

  return {
    ...user,
    id: getSubUserId(user) || user.user_id,
    subUserId: getSubUserId(user),
    participantSubUserId: getSubUserId(user),
    firstName: user.first_name || firstName,
    lastName: user.last_name || lastName,
    name: `${user.name || "-"}${isMainParticipant ? " (you)" : ""}`.replace(
      " (you) (you)",
      " (you)"
    ),
    email: user.email || "---",
    role: getRoleLabel(user.type),
    status,
    jobTitle: user.job_title || user.jobTitle || "---",
    phoneNumber: user.mobile_no || user.phoneNumber || "---",
    dateAdded: formatDate(
      user.createdAt ||
        user.created_at ||
        user.date_added ||
        user.dateAdded ||
        undefined
    ),
    lastLogin: formatDate(
      user.last_login_at || user.last_login || user.lastLogin,
      "---"
    ),
    actions: getActionItems(user, isTeamMember),
  };
};

const buildMetrics = (summary: UserManagementSummary = {}) => {
  const totalUsers = summary.total_users ?? 0;
  const activeUsers = summary.active_users ?? 0;
  const userLimit = summary.user_limit ?? 100;

  return USER_MANAGEMENT_METRICS.map((metric) => {
    if (metric.title === "Total Users") {
      return { ...metric, value: String(totalUsers) };
    }
    if (metric.title === "Active Users") {
      return { ...metric, value: String(activeUsers) };
    }
    return { ...metric, value: `${totalUsers} / ${userLimit}` };
  });
};

const getErrorMessage = (error: ServiceError) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

const UserManagementPage = () => {
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as UserManagementContext;
  const loader = useContext(LoaderContext);
  const participantId = getParticipantId(loggedInUser);
  const isTeamMember = Boolean(loggedInUser?.is_sub_participant);

  const [isAddSubUserOpen, setIsAddSubUserOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [users, setUsers] = useState<UserManagementRow[]>([]);
  const [summary, setSummary] = useState<UserManagementSummary>({});
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    searchKey: "",
  });
  const [modalMode, setModalMode] = useState("add");
  const [selectedUser, setSelectedUser] = useState<UserManagementRow | null>(
    null
  );
  const [backendError, setBackendError] = useState("");
  const [resetPasswordUser, setResetPasswordUser] =
    useState<UserManagementRow | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null
  );
  const [inviteSuccessEmail, setInviteSuccessEmail] = useState("");

  const metrics = useMemo(() => buildMetrics(summary), [summary]);
  const canInviteUsers =
    Boolean(participantId) &&
    Boolean(selectedBrand?.id) &&
    !isTeamMember &&
    (summary.remaining_team_member_slots ?? 1) > 0;

  const runWithLoader = useCallback(
    async (callback) => {
      loader?.(true);
      try {
        return await callback();
      } finally {
        loader?.(false);
      }
    },
    [loader]
  );

  const loadUsers = useCallback(async () => {
    if (!participantId) return;
    setIsLoading(true);
    try {
      const response = (await userManagementService.getTeamMembers({
        participantId,
        page: pagination.currentPage,
        row: pagination.pageSize,
        searchKey: pagination.searchKey,
      })) as ServiceResponse;
      const responseUsers = response.data?.users || [];
      const totalRecords =
        response.data?.recordsTotal ??
        response.data?.recordsFiltered ??
        response.data?.totalRecords ??
        response.data?.total ??
        response.data?.summary?.total_users ??
        responseUsers.length;
      const totalPages =
        response.data?.pageCount ??
        response.data?.totalPages ??
        Math.ceil(totalRecords / pagination.pageSize);

      setUsers(responseUsers.map((user) => mapUserRow(user, isTeamMember)));
      setSummary(response.data?.summary || {});
      setPagination((currentPagination) => ({
        ...currentPagination,
        totalPages,
        totalRecords,
      }));
    } catch (_error) {
      setUsers([]);
      setSummary({});
      setPagination((currentPagination) => ({
        ...currentPagination,
        totalPages: 0,
        totalRecords: 0,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [
    isTeamMember,
    pagination.currentPage,
    pagination.pageSize,
    pagination.searchKey,
    participantId,
  ]);

  const handleFetchUsers = useCallback(
    ({ pageIndex, pageSize, searchKey }: FetchUsersArgs) => {
      setPagination((currentPagination) => {
        const nextPagination = {
          ...currentPagination,
          currentPage: pageIndex + 1,
          pageSize,
          searchKey: searchKey || "",
        };

        if (
          nextPagination.currentPage === currentPagination.currentPage &&
          nextPagination.pageSize === currentPagination.pageSize &&
          nextPagination.searchKey === currentPagination.searchKey
        ) {
          return currentPagination;
        }

        return nextPagination;
      });
    },
    []
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleOpenAddSubUser = useCallback(() => {
    setModalMode("add");
    setSelectedUser(null);
    setBackendError("");
    setIsAddSubUserOpen(true);
  }, []);

  const handleCloseAddSubUser = useCallback(() => {
    setIsAddSubUserOpen(false);
    setSelectedUser(null);
    setBackendError("");
  }, []);

  const handleInviteSubUser = useCallback(
    async (values: ModalValues) => {
      setBackendError("");
      const payload = {
        participant_id: participantId,
        ...(modalMode === "add" ? { fk_brand_id: selectedBrand?.id } : {}),
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        ...(values.phoneNumber ? { mobile_no: values.phoneNumber } : {}),
        ...(values.jobTitle ? { job_title: values.jobTitle } : {}),
      };

      try {
        await runWithLoader(async () => {
          if (modalMode === "edit") {
            await userManagementService.updateTeamMember(selectedUser.user_id, {
              ...payload,
              participant_id: participantId,
            });
            toastr.success("Success", "Team member updated successfully");
          } else {
            await userManagementService.inviteTeamMember(payload);
            toastr.success("Success", "Invitation sent successfully");
            setInviteSuccessEmail(values.email);
          }
        });
        setIsAddSubUserOpen(false);
        setSelectedUser(null);
        await loadUsers();
      } catch (error) {
        setBackendError(getErrorMessage(error as ServiceError));
        throw error;
      }
    },
    [
      loadUsers,
      modalMode,
      participantId,
      runWithLoader,
      selectedBrand?.id,
      selectedUser?.user_id,
    ]
  );

  const handleOpenPermissions = useCallback(() => {
    setIsPermissionsOpen(true);
  }, []);

  const handleClosePermissions = useCallback(() => {
    setIsPermissionsOpen(false);
  }, []);

  const handleAction = useCallback(
    async (action: string, user: UserManagementRow) => {
      if (!user?.user_id) return;

      if (action === USER_MANAGEMENT_ACTIONS.EDIT) {
        setModalMode("edit");
        setSelectedUser(user);
        setBackendError("");
        setIsAddSubUserOpen(true);
        return;
      }

      if (action === USER_MANAGEMENT_ACTIONS.RESET_PASSWORD) {
        setResetPasswordUser(user);
        return;
      }

      try {
        if (
          action === USER_MANAGEMENT_ACTIONS.SEND_INVITE ||
          action === USER_MANAGEMENT_ACTIONS.RESEND_INVITE
        ) {
          setConfirmAction({ action, user });
          return;
        } else if (action === USER_MANAGEMENT_ACTIONS.DEACTIVATE) {
          setConfirmAction({ action, user });
          return;
        } else if (action === USER_MANAGEMENT_ACTIONS.CANCEL) {
          setConfirmAction({ action, user });
          return;
        } else if (action === USER_MANAGEMENT_ACTIONS.REACTIVATE) {
          setConfirmAction({ action, user });
          return;
        }
        await loadUsers();
      } catch (_error) {}
    },
    [loadUsers]
  );

  const actionDialogConfig = useMemo(() => {
    if (!confirmAction?.action || !confirmAction?.user) return null;

    const userEmail = confirmAction.user.email;

    if (confirmAction.action === USER_MANAGEMENT_ACTIONS.CANCEL) {
      const inviteDescription = confirmAction.user.actions?.some(
        (action) => action.value === USER_MANAGEMENT_ACTIONS.SEND_INVITE
      )
        ? "pending invitation for"
        : "invite sent to";

      return {
        title: "Cancel User?",
        confirmLabel: "Yes, Cancel",
        body: (
          <>
            The {inviteDescription} <strong>{userEmail}</strong> will be
            canceled and the user will be marked inactive.
          </>
        ),
      };
    }

    if (
      confirmAction.action === USER_MANAGEMENT_ACTIONS.SEND_INVITE ||
      confirmAction.action === USER_MANAGEMENT_ACTIONS.RESEND_INVITE
    ) {
      const isSendInviteAction =
        confirmAction.action === USER_MANAGEMENT_ACTIONS.SEND_INVITE;

      return {
        title: "Are you sure?",
        confirmLabel: isSendInviteAction ? "Yes, Send It!" : "Yes, Re-Send It!",
        primaryFirst: true,
        body: (
          <>
            An invitation email will be sent to the user. You won&apos;t be able
            to revert this action!
          </>
        ),
      };
    }

    if (confirmAction.action === USER_MANAGEMENT_ACTIONS.DEACTIVATE) {
      return {
        title: "Deactivate User?",
        confirmLabel: "Yes, Deactivate",
        body: (
          <>
            The user <strong>{userEmail}</strong> will be deactivated won&apos;t
            have access to the platform. You can activate the user again
            whenever you need.
          </>
        ),
      };
    }

    if (confirmAction.action === USER_MANAGEMENT_ACTIONS.REACTIVATE) {
      return {
        title: "Reactivate User?",
        confirmLabel: "Yes, Reactivate",
        body: (
          <>
            The user <strong>{userEmail}</strong> will be reactivated and will
            have access to the platform again.
          </>
        ),
      };
    }

    return null;
  }, [confirmAction]);

  const handleCloseActionDialog = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleCloseInviteSuccess = useCallback(() => {
    setInviteSuccessEmail("");
  }, []);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction?.action || !confirmAction?.user?.user_id) return;

    const { action, user } = confirmAction;
    const payload = { participant_id: participantId };

    try {
      if (action === USER_MANAGEMENT_ACTIONS.CANCEL) {
        await runWithLoader(async () => {
          await userManagementService.cancelInvite(user.user_id, payload);
          toastr.success("Success", "Invitation cancelled successfully");
        });
      } else if (
        action === USER_MANAGEMENT_ACTIONS.SEND_INVITE ||
        action === USER_MANAGEMENT_ACTIONS.RESEND_INVITE
      ) {
        await runWithLoader(async () => {
          await userManagementService.resendInvite(user.user_id, {
            ...payload,
            user_id: user.user_id,
          });
          toastr.success(
            "Success",
            action === USER_MANAGEMENT_ACTIONS.SEND_INVITE
              ? "Invitation sent successfully"
              : "Invitation resent successfully"
          );
        });
      } else if (action === USER_MANAGEMENT_ACTIONS.DEACTIVATE) {
        await runWithLoader(async () => {
          await userManagementService.updateStatus(user.user_id, {
            ...payload,
            status: 0,
          });
          toastr.success("Success", "Team member deactivated successfully");
        });
      } else if (action === USER_MANAGEMENT_ACTIONS.REACTIVATE) {
        await runWithLoader(async () => {
          await userManagementService.updateStatus(user.user_id, {
            ...payload,
            status: 1,
          });
          toastr.success("Success", "Team member reactivated successfully");
        });
      }

      setConfirmAction(null);
      await loadUsers();
    } catch (_error) {}
  }, [confirmAction, loadUsers, participantId, runWithLoader]);

  const handleResetPassword = useCallback(
    async (mode: string) => {
      if (!resetPasswordUser?.user_id) return;
      try {
        await runWithLoader(async () => {
          await userManagementService.resetPassword(resetPasswordUser.user_id, {
            participant_id: participantId,
            mode,
          });
          toastr.success("Success", "Password reset email sent successfully");
        });
        setResetPasswordUser(null);
      } catch (_error) {}
    },
    [participantId, resetPasswordUser?.user_id, runWithLoader]
  );

  const modalInitialValues = useMemo(() => {
    if (!selectedUser) return addSubUserInitialValues;
    return {
      firstName: selectedUser.firstName || "",
      lastName: selectedUser.lastName || "",
      email: selectedUser.email || "",
      phoneNumber: selectedUser.phoneNumber || "",
      jobTitle:
        selectedUser.jobTitle === "-" ? "" : selectedUser.jobTitle || "",
    };
  }, [selectedUser]);

  return (
    <PageLayoutComponent className="mx-0 mx-sm-2">
      <Box
        className="d-flex justify-content-between align-items-start mb-2 ml-2"
        sx={{
          flexDirection: { xs: "column", md: "row" },
          ".widget-title": { borderLeft: 0, pl: 0 },
        }}
      >
        <Box>
          <Typography variant="h5" className="widget-title mb-2">
            User Management
          </Typography>
          <Typography
            className="mb-2"
            sx={{ color: "var(--neutral-700)", fontSize: 13 }}
          >
            Manage users and their access to your account
          </Typography>
          <Button
            variant="text"
            className="link no-line mb-2 p-0 text-capitalize"
            onClick={handleOpenPermissions}
          >
            See permissions list
          </Button>
        </Box>
        {!isTeamMember ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddAlt />}
            className="py-2 text-nowrap"
            onClick={handleOpenAddSubUser}
            disabled={!canInviteUsers}
            sx={{
              borderRadius: "var(--radius-control)",
              boxShadow: "none",
              fontWeight: 700,
              minHeight: 45,
              minWidth: { xs: "100%", md: 250 },
              mt: { xs: 2, md: 0 },
            }}
          >
            Invite User
          </Button>
        ) : null}
      </Box>

      {!participantId ? (
        <Alert severity="warning" className="mb-3">
          Participant account details are unavailable for user management.
        </Alert>
      ) : null}

      <div className="mb-3">
        <div className="container-fluid px-0">
          <div className="row align-items-stretch">
            {metrics.map((metric) => (
              <UserManagementMetricCardComponent
                key={metric.title}
                {...metric}
                isLoading={isLoading}
              />
            ))}
          </div>
        </div>
      </div>

      <UserManagementTableComponent
        users={users}
        isLoading={isLoading}
        pagination={pagination}
        onFetchData={handleFetchUsers}
        onAction={handleAction}
      />

      <AddSubUserModalComponent
        open={isAddSubUserOpen}
        onClose={handleCloseAddSubUser}
        onSubmit={handleInviteSubUser}
        initialValues={modalInitialValues}
        title={modalMode === "edit" ? "Edit Sub User" : "Add new Sub User"}
        submitLabel={modalMode === "edit" ? "Save Changes" : "Continue"}
        backendError={backendError}
        disableEmail={
          modalMode === "edit" && selectedUser?.status === "Invite Sent"
        }
        showCloseIcon
      />

      <UserPermissionsModalComponent
        open={isPermissionsOpen}
        onClose={handleClosePermissions}
      />

      <ResetPasswordModalComponent
        open={Boolean(resetPasswordUser)}
        userEmail={resetPasswordUser?.email}
        onClose={() => setResetPasswordUser(null)}
        onSubmit={handleResetPassword}
      />

      {actionDialogConfig ? (
        <UserActionConfirmModalComponent
          open={Boolean(actionDialogConfig)}
          title={actionDialogConfig.title}
          confirmLabel={actionDialogConfig.confirmLabel}
          primaryFirst={actionDialogConfig.primaryFirst}
          onClose={handleCloseActionDialog}
          onConfirm={handleConfirmAction}
        >
          {actionDialogConfig.body}
        </UserActionConfirmModalComponent>
      ) : null}

      <UserActionConfirmModalComponent
        open={Boolean(inviteSuccessEmail)}
        title="User invited successfully"
        confirmLabel="Back to User Management"
        variant="success"
        showCancel={false}
        onClose={handleCloseInviteSuccess}
        onConfirm={handleCloseInviteSuccess}
      >
        <>
          The account has been created and an invitation email has been sent to{" "}
          <Box component="strong" sx={{ color: "var(--neutral-700)" }}>
            {inviteSuccessEmail}
          </Box>{" "}
          They can now access the platform using the provided credentials.
        </>
      </UserActionConfirmModalComponent>
    </PageLayoutComponent>
  );
};

export default UserManagementPage;
